import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, ArrowRight } from 'lucide-react';
import { Alert, Spinner } from '../components/shared';
import api from '../lib/api';

export default function ResetPassword() {
  const navigate = useNavigate();
  const [token, setToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const t = params.get('token');
    if (!t) {
      navigate('/forgot-password');
    } else {
      setToken(t);
    }
  }, [navigate]);

  const passwordStrength = (pw) => {
    if (pw.length === 0) return null;
    if (pw.length < 8) return { label: 'Too short', color: 'var(--error)', width: '25%' };
    if (pw.length < 12) return { label: 'Fair', color: 'var(--warning)', width: '50%' };
    if (pw.length < 16) return { label: 'Good', color: 'var(--teal)', width: '75%' };
    return { label: 'Strong', color: 'var(--success)', width: '100%' };
  };

  const strength = passwordStrength(newPassword);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);
    try {
      await api.post('/auth/reset-password', { token, newPassword });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Reset failed. The link may have expired.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'var(--ink)',
      backgroundImage: 'radial-gradient(ellipse 80% 60% at 30% 50%, rgba(45,212,191,0.05) 0%, transparent 60%)',
      padding: '24px',
    }}>
      <div style={{ width: '100%', maxWidth: 440 }}>
        {/* Brand */}
        <div style={{ textAlign: 'center', marginBottom: 40 }}>
          <div style={{ fontFamily: 'var(--font-display)', fontSize: '2rem', fontWeight: 600, color: 'var(--gold)', letterSpacing: '0.05em' }}>
            FUTURUM INNOVATION AWARDS
          </div>
        </div>

        <div className="card" style={{ padding: 36 }}>
          {!success ? (
            <>
              <div style={{ marginBottom: 28 }}>
                <h1 style={{ fontFamily: 'var(--font-display)', fontSize: '1.75rem', fontWeight: 400 }}>Set a new password</h1>
                <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', marginTop: 6 }}>
                  Choose a strong password for your account.
                </p>
              </div>

              {error && <div style={{ marginBottom: 20 }}><Alert type="error">{error}</Alert></div>}

              <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
                <div className="form-group">
                  <label className="form-label">New Password</label>
                  <div style={{ position: 'relative' }}>
                    <input
                      className="form-input"
                      type={showPass ? 'text' : 'password'}
                      placeholder="Min. 8 characters"
                      value={newPassword}
                      onChange={e => setNewPassword(e.target.value)}
                      required
                      autoFocus
                      style={{ paddingRight: 44 }}
                    />
                    <button type="button" onClick={() => setShowPass(v => !v)}
                      style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer' }}>
                      {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                  {strength && (
                    <div style={{ marginTop: 8 }}>
                      <div style={{ height: 3, background: 'rgba(255,255,255,0.08)', borderRadius: 3, overflow: 'hidden' }}>
                        <div style={{ height: '100%', width: strength.width, background: strength.color, borderRadius: 3, transition: 'width 0.3s, background 0.3s' }} />
                      </div>
                      <div style={{ fontSize: '0.72rem', color: strength.color, marginTop: 4 }}>{strength.label}</div>
                    </div>
                  )}
                </div>

                <div className="form-group">
                  <label className="form-label">Confirm Password</label>
                  <div style={{ position: 'relative' }}>
                    <input
                      className="form-input"
                      type={showConfirm ? 'text' : 'password'}
                      placeholder="Repeat your new password"
                      value={confirmPassword}
                      onChange={e => setConfirmPassword(e.target.value)}
                      required
                      style={{ paddingRight: 44 }}
                    />
                    <button type="button" onClick={() => setShowConfirm(v => !v)}
                      style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer' }}>
                      {showConfirm ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                  {confirmPassword && newPassword !== confirmPassword && (
                    <div style={{ fontSize: '0.72rem', color: 'var(--error)', marginTop: 4 }}>Passwords do not match</div>
                  )}
                  {confirmPassword && newPassword === confirmPassword && newPassword.length >= 8 && (
                    <div style={{ fontSize: '0.72rem', color: 'var(--success)', marginTop: 4 }}>✓ Passwords match</div>
                  )}
                </div>

                <button type="submit" className="btn btn-primary btn-lg" disabled={loading} style={{ width: '100%', marginTop: 8 }}>
                  {loading ? <Spinner size={18} /> : <>Update Password <ArrowRight size={16} /></>}
                </button>
              </form>
            </>
          ) : (
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '2.5rem', marginBottom: 16 }}>✅</div>
              <h1 style={{ fontFamily: 'var(--font-display)', fontSize: '1.75rem', fontWeight: 400, marginBottom: 12 }}>Password updated!</h1>
              <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', marginBottom: 28, lineHeight: 1.6 }}>
                Your password has been changed successfully. You can now sign in with your new password.
              </p>
              <Link to="/login" className="btn btn-primary btn-lg" style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                Log in with new password <ArrowRight size={16} />
              </Link>
            </div>
          )}
        </div>

        {!success && (
          <p style={{ textAlign: 'center', marginTop: 24, fontSize: '0.78rem', color: 'var(--text-muted)' }}>
            <Link to="/forgot-password" style={{ color: 'var(--text-muted)' }}>← Request a new link</Link>
          </p>
        )}
      </div>
    </div>
  );
}
